/**
 * workspaceStore.ts — estado global do workspace ativo (Zustand)
 */

import { create } from "zustand"
import { get as readCache, set as writeCache } from "idb-keyval"
import { environmentStorageKey } from "@/lib/appEnvironment"
import { useAuthStore } from "@/store/authStore"
import { captureSession } from "@/services/submissionConcurrency"
import type { AdminPermissionResponse, WorkspaceSummary, CreateWorkspacePayload } from "@/types/workspace"
import * as ws from "@/services/workspaceService"

type WorkspaceState = {
  workspaces: WorkspaceSummary[]
  active: WorkspaceSummary | null
  permissions: AdminPermissionResponse[]
  permissionsLoading: boolean

  // ações
  load: () => Promise<void>
  loadPermissions: (userId?: string | null) => Promise<void>
  create: (payload: CreateWorkspacePayload) => Promise<WorkspaceSummary>
  replace: (workspace: WorkspaceSummary) => void
  select: (id: string, userId?: string | null) => Promise<void>
  canManageWorkspace: () => boolean
  hasModulePermission: (module: number) => boolean
  clear: () => void
}

const workspaceLoads = new Map<string, Promise<void>>()
let visibleOwner: string | undefined
let permissionRequest = 0

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  active: null,
  permissions: [],
  permissionsLoading: false,

  load: async () => {
    const userId = useAuthStore.getState().user?.id
    const check = captureSession(userId)
    if (visibleOwner !== userId) {
      visibleOwner = userId
      set({ workspaces: [], active: null, permissions: [] })
    }
    const pending = workspaceLoads.get(userId!)
    if (pending) return pending
    const key = environmentStorageKey(`workspace-snapshot:${userId}`)
    const apply = (list: WorkspaceSummary[]) => {
      check()
      const activeId = ws.getActiveWorkspaceId()
      const active = (activeId ? list.find(w => w.id === activeId) : undefined) ?? list[0] ?? null
      if (active) ws.setActiveWorkspace(active.id)
      set({ workspaces: list, active })
    }
    const operation = (async () => {
      const cached = await readCache<WorkspaceSummary[]>(key).catch(() => undefined)
      check()
      if (cached) apply(cached)
      try {
        const list = await ws.getWorkspaces()
        check()
        await writeCache(key, list).catch(error => console.warn("Workspace snapshot could not be persisted", error))
        apply(list)
      } catch (error) {
        check()
        if (!cached) throw error
      }
    })()
    workspaceLoads.set(userId!, operation)
    try { await operation } finally { if (workspaceLoads.get(userId!) === operation) workspaceLoads.delete(userId!) }
  },

  loadPermissions: async (userId) => {
    const request = ++permissionRequest
    const active = get().active
    if (!active || !userId) {
      set({ permissions: [], permissionsLoading: false })
      return
    }

    if (active.role !== "Admin") {
      set({ permissions: [], permissionsLoading: false })
      return
    }

    const check = captureSession(userId, active.id)
    set({ permissions: [], permissionsLoading: true })
    try {
      const permissions = await ws.getAdminPermissionsForUser(active.id, userId)
      check()
      if (request === permissionRequest) set({ permissions, permissionsLoading: false })
    } catch {
      if (request !== permissionRequest) return
      try { check() } catch { return }
      set({ permissions: [], permissionsLoading: false })
    }
  },

  create: async (payload) => {
    const newWs = await ws.createWorkspace(payload)
    set((state) => ({ workspaces: [...state.workspaces, newWs], active: newWs, permissions: [] }))
    ws.setActiveWorkspace(newWs.id)
    return newWs
  },

  replace: (workspace) => {
    set((state) => ({
      workspaces: state.workspaces.map((item) => item.id === workspace.id ? { ...item, ...workspace } : item),
      active: state.active?.id === workspace.id ? { ...state.active, ...workspace } : state.active,
    }))
  },

  select: async (id, userId) => {
    ws.setActiveWorkspace(id)
    const found = get().workspaces.find((w) => w.id === id) ?? null
    set({ active: found, permissions: [] })
    await get().loadPermissions(userId)
  },

  canManageWorkspace: () => {
    const role = get().active?.role
    return role === "Owner" || role === "Admin"
  },

  hasModulePermission: (module) => {
    const role = get().active?.role
    if (role === "Owner") return true
    if (role !== "Admin") return false
    return get().permissions.some((permission) => permission.module === module)
  },

  clear: () => {
    permissionRequest++
    ws.clearWorkspaces()
    set({ workspaces: [], active: null, permissions: [], permissionsLoading: false })
  },
}))
